import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gql, useMutation, useQuery, useLazyQuery } from "@apollo/client";
import "./IncentivesTab.css";

// Rewards tab (formerly "Incentives"). One ledger for every reward the
// admin hands out — reward months (free subscription time), dinners,
// dance tickets, free-form perks — across paying and non-paying users.
// Backed by the socialIncentives DDB table via allSocialIncentives.
//
// The headline feature is "reward months": a grant carries a months
// count, the summary tiles total them up, and the per-user rollup
// answers "how many months have I given out, and to whom". Type /
// subscriber filters narrow the table; clicking any email filters to
// that person. Stripe account-balance credits are still managed
// per-customer in the Subscriptions tab — this is the local ledger.

// Canonical type string for free-subscription-month grants. Filters and
// totals key off rows whose `months` field is set, but keeping the type
// string stable makes the table + search behave predictably.
const REWARD_MONTHS_TYPE = "reward months";

// Quick-pick types for the grant form. "custom" swaps in a free-text
// input so one-off rewards don't need a code change.
const TYPE_PRESETS = [
  { value: REWARD_MONTHS_TYPE, label: "Reward month(s) — free subscription time" },
  { value: "dinner", label: "Dinner" },
  { value: "ticket", label: "Event / dance ticket" },
  { value: "gift", label: "Gift" },
  { value: "custom", label: "Custom…" },
];

const ALL_INCENTIVES = gql`
  query AllSocialIncentives($search: String, $limit: Int) {
    allSocialIncentives(search: $search, limit: $limit) {
      id
      createdAt
      type
      description
      amount
      currency
      captainEmail
      customerId
      createdBy
      notes
      eventId
      months
      recipientPaid
      stripeCreditApplied
      stripeCreditNote
      reversed
      reversedAt
      reversalNote
    }
  }
`;

const UNDO_INCENTIVE = gql`
  mutation UndoSocialIncentive($id: ID!) {
    undoSocialIncentive(id: $id) {
      id
      reversed
      reversedAt
      reversalNote
    }
  }
`;

const SEARCH_USERS = gql`
  query SearchSocialUsersForIncentive($query: String!) {
    searchSocialUsers(query: $query) {
      userId
      email
      firstName
      lastName
      name
      phone
      paid
      approved
      stripeEmail
    }
  }
`;

const ADD_INCENTIVE = gql`
  mutation AddSocialIncentiveCrossUser($input: SocialIncentiveInput!) {
    addSocialIncentive(input: $input) {
      id
      createdAt
      type
      description
      amount
      currency
      captainEmail
      customerId
      createdBy
      months
      recipientPaid
      stripeCreditApplied
      stripeCreditNote
    }
  }
`;

const DELETE_INCENTIVE = gql`
  mutation DeleteSocialIncentiveCrossUser($id: ID!) {
    deleteSocialIncentive(id: $id)
  }
`;

const SEARCH_DEBOUNCE_MS = 300;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatAmount = (cents, currency) => {
  if (typeof cents !== "number") return "";
  const dollars = cents / 100;
  const cur = (currency || "usd").toUpperCase();
  return `${cur === "USD" ? "$" : ""}${dollars.toFixed(2)}${
    cur === "USD" ? "" : ` ${cur}`
  }`;
};

const IncentivesTab = () => {
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [subFilter, setSubFilter] = useState("all");
  const debounceRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Debounce the table search so we don't fire a scan per keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // Pull the server max (500) so client-side type/subscriber filters and
  // the months totals see the whole ledger, not a truncated page.
  const { data, loading, error, refetch } = useQuery(ALL_INCENTIVES, {
    variables: { search: appliedSearch || null, limit: 500 },
    fetchPolicy: "network-only",
  });
  const [deleteIncentive] = useMutation(DELETE_INCENTIVE);
  const [undoIncentive] = useMutation(UNDO_INCENTIVE);
  const [undoingId, setUndoingId] = useState("");

  const rows = useMemo(() => data?.allSocialIncentives || [], [data]);

  // Distinct types present in the (searched) ledger, for the type
  // dropdown. Reward months is pinned first even before any grant
  // exists so the admin can always reach the filter.
  const typeOptions = useMemo(() => {
    const seen = new Map();
    seen.set(REWARD_MONTHS_TYPE, REWARD_MONTHS_TYPE);
    rows.forEach((r) => {
      const t = (r.type || "").trim();
      if (t && !seen.has(t.toLowerCase())) seen.set(t.toLowerCase(), t);
    });
    return Array.from(seen.values());
  }, [rows]);

  // Type + subscriber filters apply client-side on top of the server
  // text search. Subscriber status is the grant-time snapshot
  // (recipientPaid); rows logged before that field existed have no
  // snapshot and only show under "All".
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (typeFilter !== "all") {
        const t = (r.type || "").trim().toLowerCase();
        if (t !== typeFilter.toLowerCase()) return false;
      }
      if (subFilter === "subs" && r.recipientPaid !== true) return false;
      if (subFilter === "nonsubs" && r.recipientPaid !== false) return false;
      return true;
    });
  }, [rows, typeFilter, subFilter]);

  // Summary tiles + per-user months rollup, over the filtered view so
  // "filter to one user" doubles as "total months for that user".
  // Reversed rows stay visible in the table (audit trail) but their
  // months don't count toward totals — that's what keeps the numbers
  // honest after an undo.
  const stats = useMemo(() => {
    let totalMonths = 0;
    let monthGrants = 0;
    const recipients = new Set();
    const byUser = new Map();
    filteredRows.forEach((r) => {
      const who = r.captainEmail || (r.customerId ? `cust:${r.customerId}` : "(unattributed)");
      recipients.add(who);
      if (r.reversed === true) return;
      if (typeof r.months === "number" && r.months > 0) {
        totalMonths += r.months;
        monthGrants += 1;
        const entry = byUser.get(who) || { who, months: 0, grants: 0 };
        entry.months += r.months;
        entry.grants += 1;
        byUser.set(who, entry);
      }
    });
    return {
      totalMonths,
      monthGrants,
      recipientCount: recipients.size,
      monthsByUser: Array.from(byUser.values()).sort(
        (a, b) => b.months - a.months
      ),
    };
  }, [filteredRows]);

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete this reward?\n\n${row.description || row.type || row.id}`
      )
    ) {
      return;
    }
    try {
      await deleteIncentive({ variables: { id: row.id } });
      refetch();
    } catch (err) {
      window.alert(`Delete failed: ${err.message || err}`);
    }
  };

  const handleAdded = useCallback(() => {
    setShowAddModal(false);
    refetch();
  }, [refetch]);

  const handleUndo = async (row) => {
    const who = row.captainEmail || row.customerId || "this user";
    const monthsLabel =
      typeof row.months === "number" && row.months > 0
        ? `${row.months} reward month${row.months === 1 ? "" : "s"}`
        : row.description || row.type || "this reward";
    const creditWarning =
      row.stripeCreditApplied === true
        ? "Any UNUSED Stripe credit from this grant will be clawed back from their balance (already-consumed months can't be recovered)."
        : "This reward never touched Stripe, so it will just be marked reversed.";
    if (
      !window.confirm(
        `Undo ${monthsLabel} for ${who}?\n\n${creditWarning}\n\nThe row stays in the ledger marked as reversed and stops counting toward totals.`
      )
    ) {
      return;
    }
    setUndoingId(row.id);
    try {
      const { data: out } = await undoIncentive({ variables: { id: row.id } });
      const note = out?.undoSocialIncentive?.reversalNote;
      if (note) window.alert(note);
      refetch();
    } catch (err) {
      window.alert(`Undo failed: ${err.message || err}`);
    } finally {
      setUndoingId("");
    }
  };

  // Clicking an email anywhere filters the table to that person.
  const filterToUser = (email) => {
    if (email) setSearchInput(email);
  };

  return (
    <div className="incentives-tab">
      <div className="incentives-tab__header">
        <h2 className="incentives-tab__title">Rewards</h2>
        <button
          type="button"
          className="incentives-tab__btn incentives-tab__btn--primary"
          onClick={() => setShowAddModal(true)}
        >
          + Give reward
        </button>
      </div>
      <p className="incentives-tab__hint">
        Every reward you've given out — reward months, dinners, tickets,
        anything — across subscribers and non-subscribers. Click an email to
        see one person's history. Stripe account-balance credits are still
        managed per-customer in the Subscriptions tab.
      </p>

      <div className="incentives-tab__tiles">
        <div className="incentives-tab__tile">
          <div className="incentives-tab__tile-num">{filteredRows.length}</div>
          <div className="incentives-tab__tile-label">rewards</div>
        </div>
        <div className="incentives-tab__tile incentives-tab__tile--months">
          <div className="incentives-tab__tile-num">{stats.totalMonths}</div>
          <div className="incentives-tab__tile-label">
            reward months ({stats.monthGrants} grant
            {stats.monthGrants === 1 ? "" : "s"})
          </div>
        </div>
        <div className="incentives-tab__tile">
          <div className="incentives-tab__tile-num">{stats.recipientCount}</div>
          <div className="incentives-tab__tile-label">recipients</div>
        </div>
      </div>

      <div className="incentives-tab__toolbar">
        <input
          type="search"
          className="incentives-tab__search"
          placeholder="Filter by email, type, description, customerId…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <select
          className="incentives-tab__select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          title="Filter by reward type"
        >
          <option value="all">All types</option>
          {typeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          className="incentives-tab__select"
          value={subFilter}
          onChange={(e) => setSubFilter(e.target.value)}
          title="Filter by subscriber status at grant time"
        >
          <option value="all">Subs + non-subs</option>
          <option value="subs">Subscribers</option>
          <option value="nonsubs">Non-subscribers</option>
        </select>
        <span className="incentives-tab__count">
          {loading
            ? "Loading…"
            : `${filteredRows.length} entr${filteredRows.length === 1 ? "y" : "ies"}`}
        </span>
      </div>

      {error && (
        <div className="incentives-tab__error">
          Error loading rewards: {error.message}
        </div>
      )}

      {stats.monthsByUser.length > 0 && (
        <div className="incentives-tab__rollup">
          <div className="incentives-tab__rollup-title">
            Reward months by user
          </div>
          <div className="incentives-tab__rollup-rows">
            {stats.monthsByUser.map((u) => (
              <button
                key={u.who}
                type="button"
                className="incentives-tab__rollup-row"
                onClick={() => filterToUser(u.who.includes("@") ? u.who : "")}
                title="Filter the table to this user"
              >
                <span className="incentives-tab__rollup-who">{u.who}</span>
                <span className="incentives-tab__rollup-months">
                  {u.months} month{u.months === 1 ? "" : "s"}
                  <span className="incentives-tab__muted-sub">
                    {" "}
                    · {u.grants} grant{u.grants === 1 ? "" : "s"}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="incentives-tab__table-wrap">
        <table className="incentives-tab__table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Who</th>
              <th>Type</th>
              <th>Description</th>
              <th>Months</th>
              <th>Value</th>
              <th>Added by</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {!loading && filteredRows.length === 0 && (
              <tr>
                <td colSpan={8} className="incentives-tab__empty">
                  {appliedSearch || typeFilter !== "all" || subFilter !== "all"
                    ? "No rewards match the current filters."
                    : "No rewards logged yet."}
                </td>
              </tr>
            )}
            {filteredRows.map((r) => (
              <tr
                key={r.id}
                className={r.reversed ? "incentives-tab__row--reversed" : ""}
              >
                <td className="incentives-tab__date">{formatDate(r.createdAt)}</td>
                <td>
                  <div className="incentives-tab__who">
                    {r.captainEmail ? (
                      <button
                        type="button"
                        className="incentives-tab__linkbtn"
                        onClick={() => filterToUser(r.captainEmail)}
                        title="Filter to this user"
                      >
                        {r.captainEmail}
                      </button>
                    ) : (
                      <span className="incentives-tab__muted">
                        {r.customerId ? `cust:${r.customerId}` : "(unattributed)"}
                      </span>
                    )}
                    {r.recipientPaid === true && (
                      <div className="incentives-tab__badge incentives-tab__badge--sub">
                        subscriber
                      </div>
                    )}
                    {r.recipientPaid === false && (
                      <div className="incentives-tab__badge incentives-tab__badge--nonsub">
                        non-subscriber
                      </div>
                    )}
                    {r.captainEmail && r.customerId && (
                      <div className="incentives-tab__muted-sub">
                        cust:{r.customerId}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  {typeof r.months === "number" && r.months > 0 ? (
                    <>
                      <span className="incentives-tab__badge incentives-tab__badge--months">
                        {r.type || REWARD_MONTHS_TYPE}
                      </span>
                      {r.stripeCreditApplied === true && !r.reversed && (
                        <div
                          className="incentives-tab__badge incentives-tab__badge--credited"
                          title={r.stripeCreditNote || "Stripe balance credited"}
                        >
                          ✓ Stripe credited
                        </div>
                      )}
                      {r.stripeCreditApplied === false && !r.reversed && (
                        <div
                          className="incentives-tab__muted-sub"
                          title={r.stripeCreditNote || ""}
                        >
                          not credited in Stripe
                        </div>
                      )}
                    </>
                  ) : (
                    r.type || ""
                  )}
                  {r.reversed === true && (
                    <div
                      className="incentives-tab__badge incentives-tab__badge--reversed"
                      title={`${r.reversalNote || "Reversed"}${
                        r.reversedAt ? ` (${formatDate(r.reversedAt)})` : ""
                      }`}
                    >
                      reversed
                    </div>
                  )}
                </td>
                <td className="incentives-tab__description">
                  {r.description || ""}
                  {r.notes && (
                    <div className="incentives-tab__muted-sub">{r.notes}</div>
                  )}
                  {r.eventId && (
                    <div className="incentives-tab__muted-sub">
                      event: {r.eventId}
                    </div>
                  )}
                </td>
                <td className="incentives-tab__months">
                  {typeof r.months === "number" && r.months > 0 ? r.months : ""}
                </td>
                <td>{formatAmount(r.amount, r.currency)}</td>
                <td className="incentives-tab__muted">{r.createdBy || ""}</td>
                <td>
                  <div className="incentives-tab__row-actions">
                    {typeof r.months === "number" &&
                      r.months > 0 &&
                      r.reversed !== true && (
                        <button
                          type="button"
                          className="incentives-tab__btn"
                          onClick={() => handleUndo(r)}
                          disabled={undoingId === r.id}
                          title="Reverse the unused Stripe credit and mark this reward reversed"
                        >
                          {undoingId === r.id ? "Undoing…" : "Undo"}
                        </button>
                      )}
                    <button
                      type="button"
                      className="incentives-tab__btn incentives-tab__btn--danger-icon"
                      onClick={() => handleDelete(r)}
                      title="Delete this reward (removes it from history entirely — prefer Undo for months grants)"
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <AddIncentiveModal
          onClose={() => setShowAddModal(false)}
          onAdded={handleAdded}
        />
      )}
    </div>
  );
};

// Give-reward modal: two-step flow.
//   1. Search for a user (any socialUsers row, paying or not).
//   2. Confirm pick → choose type (reward months / dinner / ticket /
//      gift / custom), months count when it's a months grant, then
//      description + optional value + notes.
// addSocialIncentive only requires customerId OR captainEmail; this form
// always sends captainEmail (since picking happens via searchSocialUsers)
// plus a recipientPaid snapshot so the table can split subs vs non-subs.
const AddIncentiveModal = ({ onClose, onAdded }) => {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [typeChoice, setTypeChoice] = useState(REWARD_MONTHS_TYPE);
  const [customType, setCustomType] = useState("");
  const [months, setMonths] = useState("1");
  const [applyCredit, setApplyCredit] = useState(true);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  const [runSearch, { data: searchData, loading: searchLoading, error: searchError }] =
    useLazyQuery(SEARCH_USERS, { fetchPolicy: "network-only" });
  const [addIncentive] = useMutation(ADD_INCENTIVE);

  useEffect(() => {
    if (picked) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) return;
    debounceRef.current = setTimeout(() => {
      runSearch({ variables: { query: q } });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, picked, runSearch]);

  const results = searchData?.searchSocialUsers || [];

  const displayName = useMemo(() => {
    if (!picked) return "";
    return (
      [picked.firstName, picked.lastName].filter(Boolean).join(" ") ||
      picked.name ||
      picked.email ||
      picked.userId
    );
  }, [picked]);

  const isMonthsGrant = typeChoice === REWARD_MONTHS_TYPE;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!picked) return;
    const email = (picked.email || "").trim().toLowerCase();
    if (!email) {
      window.alert(
        "This user has no email on record — pick someone with an email so the reward can be attributed."
      );
      return;
    }
    const cleanType = (
      typeChoice === "custom" ? customType : typeChoice
    ).trim();
    if (!cleanType) {
      window.alert("Type is required.");
      return;
    }
    let monthsInt = null;
    if (isMonthsGrant) {
      monthsInt = parseInt(months, 10);
      if (!Number.isInteger(monthsInt) || monthsInt < 1 || monthsInt > 120) {
        window.alert("Months must be a whole number between 1 and 120.");
        return;
      }
    }
    // Months grants describe themselves if the admin leaves the box
    // empty; everything else still requires a description.
    let cleanDesc = description.trim();
    if (!cleanDesc && isMonthsGrant) {
      cleanDesc = `${monthsInt} reward month${monthsInt === 1 ? "" : "s"}`;
    }
    if (!cleanDesc) {
      window.alert("Description is required.");
      return;
    }
    let amountCents = null;
    const amountTrim = amount.trim();
    if (amountTrim) {
      const parsed = parseFloat(amountTrim);
      if (!Number.isFinite(parsed) || parsed < 0) {
        window.alert("Amount must be a non-negative number.");
        return;
      }
      amountCents = Math.round(parsed * 100);
    }
    setSubmitting(true);
    try {
      const { data: out } = await addIncentive({
        variables: {
          input: {
            captainEmail: email,
            type: cleanType,
            description: cleanDesc,
            amount: amountCents,
            currency: amountCents != null ? "usd" : null,
            notes: notes.trim() || null,
            months: monthsInt,
            recipientPaid:
              typeof picked.paid === "boolean" ? picked.paid : null,
            applyStripeCredit: isMonthsGrant ? applyCredit : null,
          },
        },
      });
      // Tell the admin what actually happened in Stripe — the reward
      // row saves either way, but "credited $30" vs "no active sub,
      // logged only" is the difference they care about.
      const note = out?.addSocialIncentive?.stripeCreditNote;
      if (isMonthsGrant && applyCredit && note) {
        window.alert(note);
      }
      onAdded();
    } catch (err) {
      window.alert(`Give reward failed: ${err.message || err}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="incentives-modal__backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="incentives-modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="incentives-modal__header">
          <h3 className="incentives-modal__title">Give reward</h3>
          <button
            type="button"
            className="incentives-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {!picked ? (
          <>
            <p className="incentives-modal__hint">
              Search for any user (paying or not). Pick them to continue.
            </p>
            <input
              type="search"
              className="incentives-modal__search"
              placeholder="Search by first or last name, email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            {query.trim().length > 0 && query.trim().length < 2 && (
              <div className="incentives-modal__hint">Type at least 2 characters…</div>
            )}
            {searchLoading && <div className="incentives-modal__hint">Searching…</div>}
            {searchError && (
              <div className="incentives-modal__error">
                Search failed: {searchError.message}
              </div>
            )}
            {!searchLoading && !searchError && query.trim().length >= 2 && results.length === 0 && (
              <div className="incentives-modal__hint">
                No matches for "{query.trim()}".
              </div>
            )}
            <div className="incentives-modal__results">
              {results.map((u) => {
                const name =
                  [u.firstName, u.lastName].filter(Boolean).join(" ") ||
                  u.name ||
                  u.email ||
                  u.userId;
                return (
                  <div key={u.userId} className="incentives-modal__row">
                    <div className="incentives-modal__row-main">
                      <div className="incentives-modal__row-name">{name}</div>
                      <div className="incentives-modal__row-meta">
                        <span>{u.email || "(no email)"}</span>
                        {typeof u.paid === "boolean" && (
                          <span>· {u.paid ? "subscriber" : "non-subscriber"}</span>
                        )}
                        {typeof u.approved === "boolean" && (
                          <span>· {u.approved ? "approved" : "not approved"}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="incentives-modal__btn incentives-modal__btn--primary"
                      onClick={() => setPicked(u)}
                      disabled={!u.email}
                      title={u.email ? "Pick this user" : "No email on record"}
                    >
                      Pick
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="incentives-modal__form">
            <div className="incentives-modal__picked">
              <div>
                <div className="incentives-modal__picked-name">{displayName}</div>
                <div className="incentives-modal__row-meta">
                  {picked.email || "(no email)"}
                  {typeof picked.paid === "boolean" && (
                    <span> · {picked.paid ? "subscriber" : "non-subscriber"}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="incentives-modal__btn"
                onClick={() => setPicked(null)}
                disabled={submitting}
              >
                Change
              </button>
            </div>

            <label className="incentives-modal__label">
              Reward type
              <select
                value={typeChoice}
                onChange={(e) => setTypeChoice(e.target.value)}
                disabled={submitting}
              >
                {TYPE_PRESETS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            {typeChoice === "custom" && (
              <label className="incentives-modal__label">
                Custom type
                <input
                  type="text"
                  value={customType}
                  onChange={(e) => setCustomType(e.target.value)}
                  placeholder="e.g. gas money, merch, shout-out"
                  disabled={submitting}
                  required
                />
              </label>
            )}
            {isMonthsGrant && (
              <>
                <label className="incentives-modal__label">
                  How many reward months?
                  <input
                    type="number"
                    step="1"
                    min="1"
                    max="120"
                    value={months}
                    onChange={(e) => setMonths(e.target.value)}
                    disabled={submitting}
                    required
                  />
                </label>
                <label className="incentives-modal__check">
                  <input
                    type="checkbox"
                    checked={applyCredit}
                    onChange={(e) => setApplyCredit(e.target.checked)}
                    disabled={submitting}
                  />
                  <span>
                    Also credit their Stripe balance automatically so their
                    next {parseInt(months, 10) === 1 ? "invoice is" : "invoices are"}{" "}
                    free
                  </span>
                </label>
                <p className="incentives-modal__hint" style={{ padding: 0 }}>
                  {applyCredit
                    ? "The credit only posts if they have an ACTIVE subscription — otherwise the reward is just logged and you'll see a note saying so."
                    : "Tracking only — nothing changes in Stripe. You can still credit them later via Subscriptions → Credit months."}
                </p>
              </>
            )}
            <label className="incentives-modal__label">
              Description{isMonthsGrant ? " (optional — auto-filled)" : ""}
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  isMonthsGrant
                    ? `Defaults to "${parseInt(months, 10) || 1} reward month${
                        (parseInt(months, 10) || 1) === 1 ? "" : "s"
                      }"`
                    : "May volleyball host dinner — Maple St Biscuit"
                }
                disabled={submitting}
                required={!isMonthsGrant}
              />
            </label>
            <label className="incentives-modal__label">
              Estimated value (USD, optional)
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Leave blank for non-monetary rewards"
                disabled={submitting}
              />
            </label>
            <label className="incentives-modal__label">
              Notes (optional)
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                disabled={submitting}
              />
            </label>

            <div className="incentives-modal__footer">
              <button
                type="button"
                className="incentives-modal__btn"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="incentives-modal__btn incentives-modal__btn--primary"
                disabled={submitting}
              >
                {submitting ? "Saving…" : "Give reward"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default IncentivesTab;
